import { type TFunction } from 'i18next';
import * as yup from 'yup';

import { OrganizationDTOStatus, OrganizationUpdateRequestDTOEmployeeCount, PlatformLocale } from '@/api/generated';
import { PROCESS_SETTINGS_STEP_KEYS } from '@/root/linker';
import {
  getProcessSettingsAccessInvitationValidation,
  getProcessSettingsTestPropertiesValidation
} from '@/views/ProcessSettingsForm/schema';

import { FolderFieldValues, SubOrganizationFieldValues } from './types';

export const getFolderBasicInfoValidationSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup
      .string()
      .min(
        1,
        t('validation:limits.minimum', {
          field: t('orgSettings.fields.organizationName'),
          count: 1,
          unit: t('validation:unit.character')
        })
      )
      .max(
        25,
        t('validation:limits.maximum', {
          field: t('orgSettings.fields.organizationName'),
          count: 25,
          unit: t('validation:unit.characters')
        })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.organizationName') })),

    status: yup
      .string()
      .oneOf(
        Object.keys(OrganizationDTOStatus),
        t('validation:validSelection', { field: t('orgSettings.fields.status') })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.status') }))
  }) as yup.Schema<FolderFieldValues>;

export const getSubFolderBasicInfoValidationSchema = getFolderBasicInfoValidationSchema;

export const getSubOrgBasicInfoValidationSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup
      .string()
      .min(
        1,
        t('validation:limits.minimum', {
          field: t('orgSettings.fields.organizationName'),
          count: 1,
          unit: t('validation:unit.character')
        })
      )
      .max(
        25,
        t('validation:limits.maximum', {
          field: t('orgSettings.fields.organizationName'),
          count: 25,
          unit: t('validation:unit.characters')
        })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.organizationName') })),

    legalName: yup
      .string()
      .min(
        1,
        t('validation:limits.minimum', {
          field: t('orgSettings.fields.legalName'),
          count: 1,
          unit: t('validation:unit.character')
        })
      )
      .max(
        50,
        t('validation:limits.maximum', {
          field: t('orgSettings.fields.legalName'),
          count: 50,
          unit: t('validation:unit.characters')
        })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.legalName') })),

    offices: yup
      .array()
      .of(yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.office') })))
      .min(1, t('validation:atLeastOne', { field: t('orgSettings.fields.office') }))
      .required(t('validation:mandatory', { field: t('orgSettings.fields.office') })),

    countries: yup
      .array()
      .of(yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.orgCountry') })))
      .min(1, t('validation:atLeastOne', { field: t('orgSettings.fields.orgCountry') }))
      .required(t('validation:mandatory', { field: t('orgSettings.fields.orgCountry') })),

    industry: yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.industry') })),

    employeeCount: yup
      .string()
      .oneOf(
        Object.keys(OrganizationUpdateRequestDTOEmployeeCount),
        t('validation:validSelection', { field: t('orgSettings.fields.numberOfEmployees') })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.numberOfEmployees') })),

    jiraId: yup.string().nullable(),
    pipedriveId: yup.string().nullable(),
    legacyId: yup.string().nullable(),

    status: yup
      .string()
      .oneOf(
        Object.keys(OrganizationDTOStatus),
        t('validation:validSelection', { field: t('orgSettings.fields.status') })
      )
      .required(t('validation:mandatory', { field: t('orgSettings.fields.status') }))
  }) as yup.Schema<SubOrganizationFieldValues>;

export const getRolesMembersValidationSchema = (t: TFunction) =>
  yup.object().shape({
    admins: yup
      .array()
      .of(yup.string().required(t('validation:mandatory', { field: t('orgSettings.fields.organizationAdmins') })))
      .min(1, t('validation:atLeastOne', { field: t('orgSettings.fields.organizationAdmins') })),

    userInviteEmailLanguage: yup
      .string()
      .oneOf(
        Object.keys(PlatformLocale),
        t('validation:validSelection', { field: t('orgSettings.fields.userInviteEmailLanguage') })
      )
  }) as yup.Schema<SubOrganizationFieldValues>;

export const getProcessSettingsValidationSchema = (t: TFunction) =>
  yup.object().shape({
    [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]: getProcessSettingsAccessInvitationValidation(t),
    [PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES]: getProcessSettingsTestPropertiesValidation(t)
  }) as yup.Schema<SubOrganizationFieldValues>;

export const getNormsValidationSchema = (t: TFunction) =>
  yup.object().shape({
    testValidityDays: yup
      .number()
      .min(
        0,
        t('validation:limits.minimum', {
          field: t('orgSettings.fields.accessValidFor'),
          count: 0,
          unit: t('validation:unit.days')
        })
      )
      .max(
        365 * 7,
        t('validation:limits.maximum', {
          field: t('orgSettings.fields.accessValidFor'),
          count: 365 * 7,
          unit: t('validation:unit.days')
        })
      )
      .nullable()
  }) as yup.Schema<SubOrganizationFieldValues>;
